import { FC } from 'react';
import { TranslateFn, useTranslate } from 'hooks/useTranslate';
import downloadDocument from 'utils/downloadDocument';

type IncentiveOfferOperationDetailDocumentationItemProps = {
  document?: string | { description?: string; name?: string; _id?: string };
  description?: string;
  /** True when the file is tied to the IncentiveOffer (seller identity at publish). Not removable from the operation UI if delete is added later. */
  isFromOffer?: boolean;
};

const getLabel = (
  isPending: boolean,
  document: string | { name?: string; _id?: string },
  t: TranslateFn
) => {
  if (isPending) {
    return t('PendingDocumentation');
  }
  if (typeof document === 'string') {
    return document;
  }
  if (document && typeof document === 'object' && document.name) {
    return document.name;
  }
  return t('Document');
};

const getDescription = (
  descriptionKey?: string,
  document?: string | { description?: string; _id?: string }
) => {
  if (descriptionKey) {
    return descriptionKey;
  }
  if (document && typeof document === 'object' && document.description) {
    return document.description;
  }
  return undefined;
};

const IncentiveOfferOperationDetailDocumentationItem: FC<
  IncentiveOfferOperationDetailDocumentationItemProps
> = ({ document, description, isFromOffer = false }) => {
  const { t } = useTranslate();

  const isPending = document === undefined || document === null;

  const label = getLabel(isPending, document ?? '', t);

  const descriptionText = getDescription(description, document ?? '');

  const handleDownload = () => {
    if (isPending) return;
    const documentId = typeof document === 'object' ? document?._id : document;
    const documentName =
      typeof document === 'object' ? document?.name || '' : '';
    if (documentId) {
      downloadDocument({ documentId, documentName, documents: [] });
    }
  };

  if (isPending) {
    return (
      <div className="col-12">
        <div className="doc-item doc-falta">
          <div className="doc-icon bg-light text-secondary">
            <em className="icon ni ni-file-pdf" />
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-start justify-content-between">
              <strong className="fs-13px text-break flex-grow-1 min-w-0">
                {label}
              </strong>
              <span className="doc-estado flex-shrink-0">
                {t('DocumentMissing')}
              </span>
            </div>
            {descriptionText && (
              <div className="text-soft fs-12px mt-1">{descriptionText}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12">
      <div
        className="doc-item doc-presente"
        onClick={handleDownload}
        role="button"
        tabIndex={0}
      >
        <div className="doc-icon bg-info-dim text-info">
          <em className="icon ni ni-check doc-icon-default" />
          <em className="icon ni ni-download doc-icon-hover" />
        </div>

        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-start justify-content-between">
            <strong className="fs-13px text-2-lines flex-grow-1 min-w-0">
              {label}
            </strong>
            <span className="d-flex align-items-center flex-shrink-0 flex-wrap justify-content-end">
              {isFromOffer && (
                <span className="doc-from-offer">{t('FromOffer')}</span>
              )}
              <span className="doc-estado">{t('DocumentPresent')}</span>
            </span>
          </div>

          {descriptionText && (
            <div className="text-soft fs-12px mt-0">{descriptionText}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncentiveOfferOperationDetailDocumentationItem;
